import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { propertyApi, unitApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
    Building2,
    MapPin,
    Settings,
    Users,
    CalendarDays,
    LayoutList,
    Pencil,
    Plus
} from 'lucide-react';
import { cn } from '@/lib/utils';
// import { format } from 'date-fns';

export default function PropertyDetailsPage() {
    const { id } = useParams<{ id: string }>();
    const [activeTab, setActiveTab] = useState('overview');

    const { data: property, isLoading: isPropertyLoading } = useQuery({
        queryKey: ['property', id],
        queryFn: async () => {
            const res = await propertyApi.getById(id!);
            return res.data;
        }
    });

    const { data: units } = useQuery({
        queryKey: ['property-units', id],
        queryFn: async () => {
            const res = await unitApi.getByProperty(id!);
            return res.data;
        },
        enabled: !!id
    });

    if (isPropertyLoading) return <div className="p-8 text-center">Loading property...</div>;
    if (!property) return <div className="p-8 text-center">Property not found</div>;

    return (
        <div className="space-y-6">
            {/* Property Header */}
            <div className="flex flex-col gap-4 rounded-xl bg-white p-6 shadow-sm sm:flex-row sm:items-start sm:justify-between">
                <div className="flex gap-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <Building2 className="h-8 w-8" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{property.name}</h1>
                        <div className="flex items-center gap-1 text-gray-500">
                            <MapPin className="h-4 w-4" />
                            <span>{property.address.line1}, {property.address.city}</span>
                        </div>
                        <div className="mt-2 flex gap-2">
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                                {property.unitCount} Units
                            </span>
                            <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                                {property.rentalMode || 'LongTerm'}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Link to={`/dashboard/properties/${id}/listing`}>
                        <Button variant="outline" className="gap-2">
                            <Pencil className="h-4 w-4" />
                            Edit Listing
                        </Button>
                    </Link>
                    <Button variant="default" className="gap-2">
                        <Settings className="h-4 w-4" />
                        Manage
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <TabButton
                        active={activeTab === 'overview'}
                        onClick={() => setActiveTab('overview')}
                        icon={LayoutList}
                        label="Overview"
                    />
                    <TabButton
                        active={activeTab === 'calendar'}
                        onClick={() => setActiveTab('calendar')}
                        icon={CalendarDays}
                        label="Calendar & Units"
                    />
                    <TabButton
                        active={activeTab === 'tenants'}
                        onClick={() => setActiveTab('tenants')}
                        icon={Users}
                        label="Tenants"
                    />
                </nav>
            </div>

            {/* Tab Content */}
            <div className="min-h-[400px]">
                {activeTab === 'overview' && (
                    <div className="grid gap-6 md:grid-cols-3">
                        <div className="col-span-2 space-y-6">
                            <div className="rounded-xl border border-gray-200 bg-white p-6">
                                <h3 className="mb-4 text-lg font-semibold">About Property</h3>
                                <div className="space-y-4 text-sm text-gray-600">
                                    <p>{property.listingDetails?.description || 'No description available. Click "Edit Listing" to add one.'}</p>

                                    {property.listingDetails?.photos && property.listingDetails.photos.length > 0 && (
                                        <div className="mt-4 grid grid-cols-4 gap-2">
                                            {property.listingDetails.photos.map((photo, i) => (
                                                <img key={i} src={photo} alt="Property" className="aspect-square rounded-lg object-cover" />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div>
                            {/* Side Stats */}
                            <div className="rounded-xl border border-gray-200 bg-white p-6">
                                <h3 className="mb-4 font-semibold">Occupancy</h3>
                                <div className="flex items-center justify-center py-8">
                                    <div className="text-center">
                                        <span className="text-3xl font-bold text-green-600">
                                            {Math.round((0 / (property.unitCount || 1)) * 100)}%
                                        </span>
                                        <p className="text-sm text-gray-500">Occupied</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'calendar' && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Availability Calendar</h2>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline">Day</Button>
                                <Button size="sm" variant="outline">Week</Button>
                                <Button size="sm" variant="default">Month</Button>
                            </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-500">
                            <CalendarDays className="mx-auto mb-2 h-12 w-12 opacity-20" />
                            <p>Calendar View Implementation Coming Soon</p>
                            <p className="text-sm">Will show unified view of Long-term Leases and Short-term Bookings.</p>
                        </div>

                        <div className="mt-8">
                            <h3 className="mb-4 text-lg font-semibold">Units</h3>
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {units?.map(unit => (
                                    <div key={unit.id} className="rounded-lg border bg-white p-4 shadow-sm">
                                        <h4 className="font-medium">{unit.unitNumber}</h4>
                                        <p className="text-sm text-gray-500">{unit.bedrooms} Bed, {unit.bathrooms} Bath</p>
                                        <p className="mt-2 font-semibold">${unit.monthlyRent}/mo</p>
                                    </div>
                                ))}
                                <div className="flex items-center justify-center rounded-lg border border-dashed p-4 text-gray-400 hover:bg-gray-50 cursor-pointer">
                                    <div className="text-center">
                                        <Plus className="mx-auto h-6 w-6" />
                                        <span className="text-sm">Add Unit</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Other tabs placeholders */}
                {activeTab === 'tenants' && (
                    <div className="p-8 text-center text-gray-500">Tenant Management...</div>
                )}
            </div>
        </div>
    );
}

function TabButton({ active, onClick, icon: Icon, label }: any) {
    return (
        <button
            onClick={onClick}
            className={cn(
                'group flex items-center border-b-2 px-1 py-4 text-sm font-medium',
                active
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            )}
        >
            <Icon className={cn('mr-2 h-5 w-5', active ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500')} />
            {label}
        </button>
    );
}
